<template>
  <div class="card content-wrapper p-2">
    <div class="marketing-customer-table table-component">
      <artisio-table :pagination="tableData.pagination" :sort="tableData.ordering" :items="tableData.rows"
                     :busy="tableData.loading" :fields="tableFields" show-toggle-columns
                     :checkboxes="true" :search="tableData.search" export-vuex-module-name="marketing/customer"
                     export-permission-name="export_customer" :export-attributes="exportAttributes"
                     :enable-simple-search="false" @paramsChanged="getTableData" :extra-data="extraData"
                     @selectedItemsChange="onSelectedItemsChange" filter-toggle-string="customer-table"
                     @update-toggle-columns="updateToggleColumns" @on-column-save="updateToggleColumns"
                     selectable enable-export>
        <template v-slot:search-form-placeholder>
          <search-collapse table-id="customer-table">
            <customer-search :exclude-search-fields="excludeSearchFields" :trustLevels="trustLevels"
                             :audiences="audiences"/>
          </search-collapse>
        </template>
        <template v-slot:header-buttons="{selectedItems}">
          <searchable-button-widget :model="model" attribute="name" value-field="name" class="mr-2"
                                    @select="addToAudience($event,selectedItems)" :label="false"
                                    v-permission="'change_audience'"
                                    :options="addBtnOptions"
                                    :search-handler="audienceSearchHandler"
                                    :display-field="audienceDisplayFieldFunction"
                                    ref="addToAudienceBtn"/>
        </template>
        <template slot="cell(no)" slot-scope="{index, item: customer}">
          <div class="d-flex justify-content-between">
            <span>{{ customer.customer_no }}</span>
          </div>
        </template>
        <template slot="cell(full_name)" slot-scope="{item: customer}">
          {{ customer.display_name }}
        </template>
        <template slot="cell(email)" slot-scope="{item: customer}">
          <template v-if="customer.email">
            <span class="badge border mr-1">
              <a :href="`mailto:` + customer.email"> {{ customer.email }}</a>
            </span>
          </template>
        </template>
        <template slot="cell(phone)" slot-scope="{item: customer}">
          <template v-if="customer.phone">
            <span class="badge border mr-1" v-for="(phone, index) in customer.phone.split(',')"
                  :key="`grid-phone-${index}`">
              {{ phone }}
            </span>
          </template>
        </template>
        <template slot="cell(address)" slot-scope="{item: customer}">
          <div v-if="customer.display_billing_address"
               v-html="$options.filters.splitAddressForMultiline(customer.display_billing_address)">
          </div>
        </template>
        <template slot="cell(status)" slot-scope="{item: customer}">
          <span v-as-status="customer.status">{{ customer.status | removeUnderscores | toUpperCaseFirst }}</span>
        </template>
        <template slot="cell(type)" slot-scope="{item: customer}">
          {{ customer.type | removeUnderscores | toUpperCaseFirst }}
        </template>
        <template slot="cell(account_managers)" slot-scope="{item: customer}">
          <template v-if="customer.account_managers && customer.account_managers.length > 0">
            {{ customer.account_managers[0].display_name }}
          </template>
        </template>
        <template slot="cell(optin)" slot-scope="{item: customer}">
          <span v-html="$options.filters.asBadgeBoolean(customer.optin)"/>
        </template>
        <template slot="cell(gender)" slot-scope="{item: customer}">
          {{ customer.gender | toUpperCaseFirst }}
        </template>
        <template slot="cell(enable_estimate_request)" slot-scope="{item: customer}">
          <span v-html="$options.filters.asBadgeBoolean(customer.enable_estimate_request)"/>
        </template>
        <template slot="cell(is_company)" slot-scope="{item: customer}">
          <span v-html="$options.filters.asBadgeBoolean(customer.is_company)"/>
        </template>
        <template slot="cell(sms_optin)" slot-scope="{item: customer}">
          <span v-html="$options.filters.asBadgeBoolean(customer.sms_optin)"/>
        </template>
        <template slot="cell(mark_optin)" slot-scope="{item: customer}">
          <span v-html="$options.filters.asBadgeBoolean(customer.mark_optin)"/>
        </template>
        <template slot="cell(is_vendor)" slot-scope="{item: customer}">
          <span v-html="$options.filters.asBadgeBoolean(customer.is_vendor)"/>
        </template>
        <template slot="cell(created_data)" slot-scope="{item: customer}">
          {{ customer.created_data }}
        </template>
        <template slot="cell(updated_data)" slot-scope="{item: customer}">
          {{ customer.updated_data }}
        </template>
        <template slot="cell(shipping_address)" slot-scope="{item: customer}">
          <div v-if="customer.display_shipping_address"
               v-html="$options.filters.splitAddressForMultiline(customer.display_shipping_address)">
          </div>
        </template>
        <template slot="cell(tags)" slot-scope="{item: customer}">
          <div v-if="customer.tags">
            <span class="badge badge-info mr-2" v-for="tag in customer.tags.split(',')"
                  :key="`customer-table-tag-${customer.uuid}-${tag}`">{{ tag }}</span>
          </div>
        </template>
        <template slot="cell(trust_level)" slot-scope="{item: customer}">
          {{ customer.trust_level_display_name }}
        </template>
        <template slot="cell(birth_date)" slot-scope="{item: customer}">
          {{ customer.birth_date | toDate }}
        </template>
        <template slot="cell(credit_card_verified_at)" slot-scope="{item: customer}">
          {{ customer.credit_card_verified_at | toDateTime }}
        </template>

        <template slot="cell(personal_identification_number)" slot-scope="{item: customer}">
          <template v-if="customer.identification">
            {{ customer.identification.personal_identification_number }}
          </template>
        </template>

        <template slot="cell(document_type)" slot-scope="{item: customer}">
          <template v-if="customer.identification">
            {{ customerIdentificationTypesMap[customer.identification.document_type] }}
          </template>
        </template>

        <template slot="cell(document_number)" slot-scope="{item: customer}">
          <template v-if="customer.identification">
            {{ customer.identification.document_number }}
          </template>
        </template>

        <template slot="cell(document_issue_date)" slot-scope="{item: customer}">
          <template v-if="customer.identification">
            {{ customer.identification.document_issue_date }}
          </template>
        </template>

        <template slot="cell(document_expiry_date)" slot-scope="{item: customer}">
          <template v-if="customer.identification">
            {{ customer.identification.document_expiry_date }}
          </template>
        </template>

        <template slot="cell(date_of_birth)" slot-scope="{item: customer}">
          <template v-if="customer.identification">
            {{ customer.identification.date_of_birth }}
          </template>
        </template>

        <template slot="cell(citizenship_code)" slot-scope="{item: customer}">
          <template v-if="customer.identification && customer.identification.citizenship">
            {{ customer.identification.citizenship.name }}
          </template>
        </template>

        <template slot="cell(residency_code)" slot-scope="{item: customer}">
          <template v-if="customer.identification && customer.identification.residency">
            {{ customer.identification.residency.name }}
          </template>
        </template>

        <template slot="cell(country_of_birth_code)" slot-scope="{item: customer}">
          <template v-if="customer.identification && customer.identification.country_of_birth">
            {{ customer.identification.country_of_birth.name }}
          </template>
        </template>

        <template slot="cell(place_of_issue)" slot-scope="{item: customer}">
          <template v-if="customer.identification">
            {{ customer.identification.place_of_issue }}
          </template>
        </template>

        <template slot="cell(company_reg_number)" slot-scope="{item: customer}">
          <template v-if="customer.identification">
            {{ customer.identification.company_reg_number }}
          </template>
        </template>

        <template slot="cell(audiences)" slot-scope="{item: customer}">
          <template v-if="customer.audiences">
            <span class="badge border mr-1" v-for="audience in customer.audiences">
              {{ audience.name }}
            </span>
          </template>
        </template>

        <template slot="cell(billing_address_address_1)" slot-scope="{item: customer}">
          <template v-if="customer.billing_address">
            {{ customer.billing_address.address_1 }}
          </template>
        </template>
        <template slot="cell(billing_address_address_2)" slot-scope="{item: customer}">
          <template v-if="customer.billing_address">
            {{ customer.billing_address.address_2 }}
          </template>
        </template>
        <template slot="cell(billing_address_city)" slot-scope="{item: customer}">
          <template v-if="customer.billing_address">
            {{ customer.billing_address.city }}
          </template>
        </template>
        <template slot="cell(billing_address_county)" slot-scope="{item: customer}">
          <template v-if="customer.billing_address">
            {{ customer.billing_address.county }}
          </template>
        </template>
        <template slot="cell(billing_address_state)" slot-scope="{item: customer}">
          <template v-if="customer.billing_address">
            {{ customer.billing_address.state }}
          </template>
        </template>
        <template slot="cell(billing_address_zip_code)" slot-scope="{item: customer}">
          <template v-if="customer.billing_address">
            {{ customer.billing_address.zip_code }}
          </template>
        </template>
        <template slot="cell(billing_address_email)" slot-scope="{item: customer}">
          <template v-if="customer.billing_address">
            {{ customer.billing_address.email }}
          </template>
        </template>
        <template slot="cell(billing_address_country)" slot-scope="{item: customer}">
          <template v-if="customer.billing_address && customer.billing_address.country">
            {{ customer.billing_address.country.name }}
          </template>
        </template>

        <template slot="cell(bank_account_name)" slot-scope="{item: customer}">
          <template v-if="customer.bank_account">
            {{ customer.bank_account.account_name }}
          </template>
        </template>

        <template slot="cell(bank_account_number)" slot-scope="{item: customer}">
          <template v-if="customer.bank_account">
            {{ customer.bank_account.account_number }}
          </template>
        </template>

        <template slot="cell(bank_sort_code)" slot-scope="{item: customer}">
          <template v-if="customer.bank_account">
            {{ customer.bank_account.sort_code }}
          </template>
        </template>

        <template slot="cell(bank_name)" slot-scope="{item: customer}">
          <template v-if="customer.bank_account">
            {{ customer.bank_account.bank_name }}
          </template>
        </template>

        <template slot="cell(iban)" slot-scope="{item: customer}">
          <template v-if="customer.bank_account">
            {{ customer.bank_account.iban }}
          </template>
        </template>

        <template slot="cell(swift)" slot-scope="{item: customer}">
          <template v-if="customer.bank_account">
            {{ customer.bank_account.swift }}
          </template>
        </template>

        <template slot="cell(bank_address)" slot-scope="{item: customer}">
          <template v-if="customer.bank_account">
            {{ customer.bank_account.address }}
          </template>
        </template>

        <template slot="cell(bank_address_2)" slot-scope="{item: customer}">
          <template v-if="customer.bank_account">
            {{ customer.bank_account.address_2 }}
          </template>
        </template>

        <template slot="cell(bank_address_3)" slot-scope="{item: customer}">
          <template v-if="customer.bank_account">
            {{ customer.bank_account.address_3 }}
          </template>
        </template>

        <template slot="cell(bank_first_name)" slot-scope="{item: customer}">
          <template v-if="customer.bank_account">
            {{ customer.bank_account.first_name }}
          </template>
        </template>

        <template slot="cell(bank_last_name)" slot-scope="{item: customer}">
          <template v-if="customer.bank_account">
            {{ customer.bank_account.last_name }}
          </template>
        </template>

        <template slot="cell(bank_email)" slot-scope="{item: customer}">
          <template v-if="customer.bank_account">
            {{ customer.bank_account.email }}
          </template>
        </template>

        <template slot="cell(shipping_address_address_1)" slot-scope="{item: customer}">
          <template v-if="customer.shipping_address">
            {{ customer.shipping_address.address_1 }}
          </template>
        </template>
        <template slot="cell(shipping_address_address_2)" slot-scope="{item: customer}">
          <template v-if="customer.shipping_address">
            {{ customer.shipping_address.address_2 }}
          </template>
        </template>
        <template slot="cell(shipping_address_city)" slot-scope="{item: customer}">
          <template v-if="customer.shipping_address">
            {{ customer.shipping_address.city }}
          </template>
        </template>
        <template slot="cell(shipping_address_county)" slot-scope="{item: customer}">
          <template v-if="customer.shipping_address">
            {{ customer.shipping_address.county }}
          </template>
        </template>
        <template slot="cell(shipping_address_state)" slot-scope="{item: customer}">
          <template v-if="customer.shipping_address">
            {{ customer.shipping_address.state }}
          </template>
        </template>
        <template slot="cell(shipping_address_zip_code)" slot-scope="{item: customer}">
          <template v-if="customer.shipping_address">
            {{ customer.shipping_address.zip_code }}
          </template>
        </template>
        <template slot="cell(shipping_address_email)" slot-scope="{item: customer}">
          <template v-if="customer.shipping_address">
            {{ customer.shipping_address.email }}
          </template>
        </template>
        <template slot="cell(shipping_address_country)" slot-scope="{item: customer}">
          <template v-if="customer.shipping_address && customer.shipping_address.country">
            {{ customer.shipping_address.country.name }}
          </template>
        </template>


      </artisio-table>
    </div>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from 'vuex';
  import ArtisioTable from '@/core/components/ams-table/ArtisioTable';
  import {MARKETING_CUSTOMER_TABLE_NAME} from '../../constants';
  import CustomerSearch from './CustomerSearch';
  import SearchCollapse from '@/core/components/search-collapse/SearchCollapse';
  import i18n from '@/shared/i18n';
  import _ from 'lodash';
  import {Formatter} from '@/core/services/formatter.service';
  import SearchableButtonWidget from '@/core/widgets/SearchableButtonWidget';
  import CustomerForm from '@/modules/customer/models/CustomerForm.model';
  import MarketingService from "@/modules/marketing/MarketingService";

  const {mapState, mapActions} = createNamespacedHelpers('marketing/customer');
  const {mapState: globalMapState, mapGetters: globalMapGetters} = createNamespacedHelpers('global');

  export default {
    name: "CustomerList",
    components: {SearchCollapse, CustomerSearch, ArtisioTable, SearchableButtonWidget},
    props: {
      hideActions: {
        type: Boolean,
        default: false
      },
      excludeSearchFields: {
        type: Array,
        default: () => []
      },
      excludeColumns: {
        type: Array,
        default: () => []
      },
      selectable: {
        type: Boolean,
        default: false
      }
    },
    data: () => {
      return {
        model: new CustomerForm(),
        selectedItems: new Set(),
        audiences: []
      }
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData,
        toggleColumns: state => state.tableData.toggleColumns,
        tableSource: state => state.tableSource,
      }),
      ...globalMapState({
        enableContinuousAuction: state => state.enableContinuousAuction,
        hasDataCollectionProvider: state => state.data_collection_provider_info.has_data_collection_provider,
        countries: state => state.countries,
        customerIdentificationTypes: state => state.customerIdentificationTypes,
        trustLevels: state => state.currentBranch.trust_levels ? state.currentBranch.trust_levels.sort((a, b) => {
          if (a.credit_limit > b.credit_limit) {
            return 1;
          } else if (b.credit_limit > a.credit_limit) {
            return -1;
          }
          return 0;
        }).map((trustLevel) => {
          return {
            uuid: trustLevel.uuid,
            name: `${trustLevel.name} | Limit(${Formatter.asCurrency(trustLevel.credit_limit)}) | Threshold(${Formatter.asCurrency(trustLevel.threshold)})`,
            value: trustLevel.uuid,
            exclude_from_auto_assignment: trustLevel.exclude_from_auto_assignment,
            credit_limit: trustLevel.credit_limit
          }
        }) : []
      }),
      customerIdentificationTypesMap: state => {
        return Object.fromEntries(state.customerIdentificationTypes.map(c => ([c.value, c.key])))
      },
      ...globalMapGetters(['countryMap']),
      tableFields() {
        return _.cloneDeep(this.toggleColumns.fields)
          .filter(field => !this.excludeColumns.includes(field.key) && field.isVisible)
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: this.excludeColumns,
          tableName: MARKETING_CUSTOMER_TABLE_NAME
        }
      },
      addBtnOptions() {
        if (this.selectedItems.size === 0) {
          return {placeholder: i18n.t('Add All to Audience'), dropdownAutoWidth: true}
        } else {
          return {placeholder: i18n.t('Add Selected to Audience'), dropdownAutoWidth: true}
        }
        // this.$forceUpdate();
      },
      exportAttributes() {
        let mapping = {
          no: 'customer_no',
          full_name: 'display_name',
          address: 'display_billing_address',
          shipping_address: 'display_shipping_address',
          account_managers: 'account_managers[[display_name]]',
          audiences: 'audiences[[name]]',
          interest_categories: 'interest_categories[[active_translation_name]]',
          trust_level: 'trust_level_display_name',
          personal_identification_number: 'identification.personal_identification_number',
          document_type: 'identification.document_type',
          document_number: 'identification.document_number',
          document_issue_date: 'identification.document_issue_date',
          document_expiry_date: 'identification.document_expiry_date',
          date_of_birth: 'identification.date_of_birth',
          citizenship_code: 'identification.citizenship.name',
          residency_code: 'identification.residency.name',
          country_of_birth_code: 'identification.country_of_birth.name',
          place_of_issue: 'identification.place_of_issue',
          company_reg_number: 'identification.company_reg_number',
          billing_address_country: 'billing_address.country.name',
          billing_address_address_1: 'billing_address.address_1',
          billing_address_address_2: 'billing_address.address_2',
          billing_address_city: 'billing_address.city',
          billing_address_county: 'billing_address.county',
          billing_address_state: 'billing_address.state',
          billing_address_zip_code: 'billing_address.zip_code',
          billing_address_email: 'billing_address.email',
          bank_account_name: 'bank_account.account_name',
          bank_account_number: 'bank_account.account_number',
          bank_sort_code: 'bank_account.sort_code',
          bank_name: 'bank_account.bank_name',
          iban: 'bank_account.iban',
          swift: 'bank_account.swift',
          bank_address: 'bank_account.address',
          bank_address_2: 'bank_account.address_2',
          bank_address_3: 'bank_account.address_3',
          bank_first_name: 'bank_account.first_name',
          bank_last_name: 'bank_account.last_name',
          bank_email: 'bank_account.email',
          shipping_address_country: 'shipping_address.country.name',
          shipping_address_address_1: 'shipping_address.address_1',
          shipping_address_address_2: 'shipping_address.address_2',
          shipping_address_city: 'shipping_address.city',
          shipping_address_county: 'shipping_address.county',
          shipping_address_state: 'shipping_address.state',
          shipping_address_zip_code: 'shipping_address.zip_code',
          shipping_address_email: 'shipping_address.email',
        }
        let titleMapping = {
          'iban': 'IBAN'
        }
        let cols = {};
        for (let col of this.toggleColumns.defaultFields) {
          cols[mapping[col] || col] = i18n.t(titleMapping[col] || col.split('_').map(x=>x.replace(/^./, x[0].toUpperCase())).join(' '));
        }
        return cols;
      }
    },
    methods: {
      ...mapActions(['getTableData', 'updateToggleColumns', 'addCustomersToAudience']),
      onSelectedItemsChange(selectedItems) {
        this.selectedItems = selectedItems;
      },
      audienceSearchHandler(search) {
        return MarketingService.getAudiences({
          name: search,
          ordering: 'name'
        });
      },
      audienceDisplayFieldFunction(item) {
        return item.name;
      },
      async addToAudience(audience) {
        let res = await this.addCustomersToAudience({audience_uuid: audience.uuid, customers: this.selectedItems});
        if (!res.success) {
          this.$toast('danger', res.toastError || i18n.t('Failed to add customers to audience') + ' ' + audience.name);
        } else {
          this.$toast('success', i18n.t('Customers successfully added to audience') + ' ' + audience.name);
        }
      }
    },
    async beforeMount() {
      let res = await MarketingService.getAudiences();
      if (res.success) {
        this.audiences = res.body.results;
      }
    },
    mounted() {
      this.getTableData();
    }
  }
</script>

<style lang="scss" scoped>


  .red-flag-icon {
    cursor: pointer;
    outline: none !important;
  }

  .artisio-table {
    /deep/ {
      .no-col {
        min-width: 160px;
      }

      .fullname-col {
        min-width: 130px;
      }

      .company-name-col {
        min-width: 130px;
      }

      .email-col {
        min-width: 150px;

      }

      .address-col {
        min-width: 250px;
      }

      .phone-col {
        min-width: 100px;
      }

      .status-col {
        min-width: 100px;
      }

      .activity-col {
        min-width: 100px;
      }

      .account-managers-col {
        min-width: 140px;
      }

      .is-company-col {
        min-width: 100px;
      }

      .sms-optin-col {
        min-width: 100px;
      }

      .mark-optin-col {
        min-width: 100px;
      }

      .personal-id-col {
        min-width: 100px;
      }

      .shipping-address-col {
        min-width: 100px;
      }

      .enable-estimate-request-col {
        min-width: 100px;
      }

      .created-data-col {
        min-width: 100px;
      }

      .updated-data-col {
        min-width: 100px;
      }

      .tags-col {
        min-width: 100px;
      }

      .interest-categories-col {
        min-width: 100px;
      }

      .optin-col {
        min-width: 100px;
      }

      .is-vendor-col {
        min-width: 100px;
      }

      .birth-date-col {
        min-width: 100px;
      }
    }
  }


  .red-flag-icon {
    cursor: pointer;
    outline: none !important;
  }

  .red-flag-comment {
    /deep/ > p {
      margin: 0;
    }
  }

</style>
